import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Checkbox } from '@/components/ui/checkbox';
import { useAccessToken } from '@/services/auth';

import type { MatingRecord } from '../../types';
import {
  matingEmbryosOptions,
  toEmbryoRecordKey,
  useClientOnFarmSelectionContext,
} from './clientOnFarmSelectionContext';

const display = (value: string | number | null | undefined) =>
  value ?? '\u2014';

export const ClientOnFarmEmbryoDetail = ({ data }: { data?: MatingRecord }) => {
  const { t } = useTranslation();
  const { selectedKeys, visibleKeys, search, readOnly, setEmbryosSelected } =
    useClientOnFarmSelectionContext();

  const token = useAccessToken();
  const {
    data: embryos,
    isLoading,
    isError,
  } = useQuery({
    ...matingEmbryosOptions(
      token,
      { id: data?.id ?? '', accountId: data?.accountId ?? null },
      search,
    ),
    enabled: !!token && !!data?.id,
  });

  const visibleEmbryos = useMemo(
    () =>
      visibleKeys
        ? (embryos ?? []).filter((embryo) =>
            visibleKeys.has(toEmbryoRecordKey(embryo)),
          )
        : (embryos ?? []),
    [embryos, visibleKeys],
  );

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-6 py-3 text-xs text-gray-500">
        <i className="pi pi-spin pi-spinner text-xs" />
        {t('onFarmImplant.sourceCard.loadingEmbryos')}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="px-6 py-3 text-xs text-red-600">
        {t('onFarmImplant.sourceCard.loadEmbryosError')}
      </div>
    );
  }

  return (
    <div className="px-6 py-2">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-gray-500">
            <th className="w-8 py-1" />
            <th className="py-1">{t('onFarmImplant.sourceCard.embryo')}</th>
            <th className="py-1">{t('onFarmImplant.sourceCard.cane')}</th>
            <th className="py-1 text-right">
              {t('onFarmImplant.sourceCard.straw')}
            </th>
            <th className="py-1 text-right">
              {t('onFarmImplant.sourceCard.stage')}
            </th>
            <th className="py-1 text-right">
              {t('onFarmImplant.sourceCard.grade')}
            </th>
          </tr>
        </thead>
        <tbody>
          {visibleEmbryos.map((embryo) => {
            const key = toEmbryoRecordKey(embryo);
            return (
              <tr key={key} className="border-t border-gray-100">
                <td className="py-1">
                  <Checkbox
                    aria-label={t('onFarmImplant.sourceCard.selectEmbryo', {
                      embryo: embryo.embryoName ?? embryo.embryoId,
                    })}
                    checked={selectedKeys.has(key)}
                    disabled={readOnly}
                    onCheckedChange={(checked) =>
                      setEmbryosSelected([key], checked)
                    }
                  />
                </td>
                <td className="py-1 font-medium text-gray-900">
                  {display(embryo.embryoName)}
                </td>
                <td className="py-1">{display(embryo.cane)}</td>
                <td className="py-1 text-right">
                  {display(embryo.strawNumber)}
                </td>
                <td className="py-1 text-right">{display(embryo.stage)}</td>
                <td className="py-1 text-right">{display(embryo.grade)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
